import { useHttpClient, useExperiments } from '@wix/fe-essentials-standalone';
import {
  ADD_AUTHORIZATION_HEADERS,
  CREATE_CONTACT,
  GET_INITIALIZE_DATA,
  ADD_COMMENT,
  DELETE_COMMENT,
  UPDATE_COMMENT,
  REPLY_TO_COMMENT,
  CLIENT_REPLY_TO_COMMENT,
} from './constants';

export default (config: {
  feedbackId: string;
  metaSiteId: string;
  pMetaSiteId: string;
  siteId: string;
  editorType: string;
  siteName: string;
  ownerId: string;
  feedbackCreatorUserGuid: string;
  linkKey: string;
  instance: string;
}) => {
  const {
    pMetaSiteId,
    feedbackId,
    metaSiteId,
    siteId,
    editorType,
    siteName,
    ownerId,
    feedbackCreatorUserGuid,
    linkKey,
    instance,
  } = config;
  const { experiments } = useExperiments({ readOnly: true });
  const httpClient = useHttpClient();
  const shouldAddAuthorizationHeader = experiments.enabled(
    ADD_AUTHORIZATION_HEADERS,
  );
  const getHeadersByExperiment = () => {
    return shouldAddAuthorizationHeader
      ? {
          headers: {
            Authorization: instance,
          },
        }
      : {};
  };
  const createContact = async ({
    userName,
    userEmail,
  }: {
    userName: string;
    userEmail?: string;
  }) => {
    const [first, last] = userName.split(' ');
    const { data } = await httpClient.post<{ contact: { contactId: string } }>(
      `${CREATE_CONTACT}`,
      {
        first,
        last,
        email: userEmail,
        linkKey,
      },
      getHeadersByExperiment(),
    );
    return data;
  };

  const getInitializeData = async () => {
    const { data } = await httpClient.get<{
      comments: any[];
      userInfo: { userName: string; contactId: string; isAnonymous?: boolean };
    }>(
      `${GET_INITIALIZE_DATA}?metaSiteId=${metaSiteId}&pMetaSiteId=${pMetaSiteId}`,
      getHeadersByExperiment(),
    );
    return data;
  };

  const addComment = async ({
    message,
    commentLocation,
    status,
    shouldSchedule,
    userInfo,
  }: {
    message: string;
    commentLocation: any;
    status: string;
    shouldSchedule: boolean;
    userInfo: any;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      `${ADD_COMMENT}`,
      {
        feedbackId,
        metaSiteId,
        pMetaSiteId,
        siteId,
        editorType,
        siteName,
        ownerId,
        feedbackCreatorUserGuid,
        ...(shouldSchedule && { shouldSchedule }),
        comment: {
          message,
          commentLocation,
          status,
          userInfo,
        },
      },
      getHeadersByExperiment(),
    );
    return data.comment;
  };

  const deleteComment = async (commentId: string) => {
    const { data } = await httpClient.post(
      `${DELETE_COMMENT}`,
      {
        feedbackId,
        metaSiteId,
        commentId,
      },
      getHeadersByExperiment(),
    );
    return data;
  };

  const updateComment = async ({
    id,
    message,
    commentLocation,
    commenterId,
    status,
    revision,
    fields,
  }: {
    id: string;
    feedbackId: string;
    message: string;
    commentLocation: any;
    commenterId: string;
    status: string;
    revision: string;
    fields: any;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      `${UPDATE_COMMENT}`,
      {
        feedbackId,
        commentId: id,
        metaSiteId,
        commenterId,
        comment: {
          message,
          commentLocation,
          status,
          revision,
        },
        fields,
      },
      getHeadersByExperiment(),
    );
    return data.comment;
  };

  const replyToComment = async ({
    id,
    revision,
    replyText,
    userInfo,
    commenterId,
    isOwner,
  }: {
    id: string;
    revision: string;
    replyText: string;
    userInfo: any;
    commenterId: string;
    isOwner: boolean;
  }) => {
    const { data } = await httpClient.post<{ comment: any }>(
      `${
        shouldAddAuthorizationHeader
          ? CLIENT_REPLY_TO_COMMENT
          : REPLY_TO_COMMENT
      }`,
      {
        feedbackId,
        commentId: id,
        metaSiteId,
        revision,
        isOwner,
        siteName,
        ownerId,
        feedbackCreatorUserGuid,
        editorType,
        siteId,
        commenterInfo: userInfo,
        commenterId,
        reply: {
          message: replyText,
        },
        pMetaSiteId,
      },
      getHeadersByExperiment(),
    );

    return data.comment;
  };

  return {
    addComment,
    getInitializeData,
    createContact,
    deleteComment,
    updateComment,
    replyToComment,
  };
};
